import { defineStore } from 'pinia'
import { ErrorCodes, MetaErrorCodes, type BaseError, type Meta } from '@cplug/frontend-core/models'
import { BaseCRUDStore } from '@cplug/frontend-core/stores'
import { CPERPSettingsGateway } from '../gateways/CPERPSettingsGateway'
import type { ERPSettings } from '../models/ERPSettings'
import { PeriodUnitType } from '../models/ConsumptionSheetERPSettings'
import useThemePreferenceStore from './useThemePreferemceStore'
import useCompanyStore from '~/domain/dashboard/company/stores/useCompanyStore'
import { useObjectClone } from '~/shared/composables/useObjectClone'

class ERPSettingsStore extends BaseCRUDStore<ERPSettings> {
  protected gateway = CPERPSettingsGateway.instance()

  protected findErrors (model: ERPSettings): BaseError[] {
    const defaultErrors = (meta: Meta): BaseError => ({ code: ErrorCodes.DataValidation, meta })
    const errors: BaseError[] = []

    if (model.customer?.isAllowedProvideNewCustomerForAllCompanies === false && (model.customer?.companies?.length ?? 0) <= 0) {
      errors.push(defaultErrors({ 'customer.companies': MetaErrorCodes.Empty }))
    }

    if (model.supplier?.enableNewSuppliersForAllCompanies === false && (model.supplier?.companies?.length ?? 0) <= 0) {
      errors.push(defaultErrors({ 'supplier.companies': MetaErrorCodes.Minimum }))
    }

    const periodUnit = model.consumptionSheet?.expirationSettings?.periodUnit
    const isNoExpiration = !periodUnit || periodUnit === PeriodUnitType.NoExpiration

    if (model.consumptionSheet?.isEnabled && !isNoExpiration) {
      if (model.consumptionSheet?.expirationSettings?.amount && !periodUnit) {
        errors.push(defaultErrors({ 'consumptionSheet.expirationSettings.periodUnit': MetaErrorCodes.Empty }))
      }

      if (!model.consumptionSheet?.expirationSettings?.amount) {
        errors.push(defaultErrors({ 'consumptionSheet.expirationSettings.amount': MetaErrorCodes.Empty }))
      }
    }

    return errors
  }
}

const useERPSettingsStore = defineStore('settings/erp', () => {
  const erpSettingsStore = new ERPSettingsStore()
  const fields = erpSettingsStore.defaultStore()

  async function getSettings (): Promise<void> {
    const result = await CPERPSettingsGateway.instance().getSettings()

    if (result.isOk()) {
      fields.model.value = result.getValue()
    }
  }

  async function save (model: ERPSettings): Promise<ERPSettings | boolean> {
    if (!fields.validate(model)) {
      return Promise.resolve(false)
    }

    if (model.theme) {
      const themeStore = useThemePreferenceStore()
      themeStore.setTheme(model.theme)
    }

    const filteredModel = useObjectClone(model)
    const companyStore = useCompanyStore()
    await companyStore.getPage()

    if ((companyStore.pageList?.length ?? 0) <= 1) {
      filteredModel.supplier = undefined
      filteredModel.carrier = undefined
    }

    const result = await CPERPSettingsGateway.instance().updateSettings(filteredModel)

    if (result.isOk()) {
      fields.model.value = result.getValue()
      return result.getValue()
    }

    return result.isOk()
  }

  return { ...fields, save, getSettings }
})

export default useERPSettingsStore
